/**
 * Environment Utility - Aegis Foundation Kit
 * 
 * Centralized logic to determine if the current view/process is part of
 * a limited "Evaluation/Demo" state or a "Live/Production" state.
 */

import type { User } from "@supabase/supabase-js";

/**
 * Routes that are strictly considered evaluation/demo views.
 * These usually feature mock data or read-only simulations.
 */
export const DEMO_ROUTES = [
    "/app/plan-demo",
    "/app/example-scenario",
    "/partner/demo"
];

/**
 * Standard environment types
 */
export type EnvironmentState = "live" | "demo";

/**
 * Deduces the current environment based on the path and user profile.
 * 
 * @param pathname The currently active URL path
 * @param user The authenticated user object (optional)
 * @returns The deduced environment state
 */
export function getEnvironmentState(pathname: string, user: User | null): EnvironmentState {
    // 1. Explicit demo routes are always demo
    if (DEMO_ROUTES.includes(pathname)) {
        return "demo";
    }

    // 2. Certain landing pages might be demo-like but eventually become production
    // For institutional dashboard, if they've signed in, it's their production landing zone
    // even if the content says "Evaluation" (as that's their specific phase).
    // However, we want the global indicator to reflect the "Productive" use of the platform.
    if (pathname === "/app/institutional-dashboard" && user) {
        return "live";
    }

    // 3. Admin and Partner areas are production tools
    if (pathname.startsWith("/admin") || (pathname.startsWith("/partner") && !pathname.includes("/demo"))) {
        return "live";
    }

    // 4. Main app routes for signed-in users are production
    if (pathname.startsWith("/app") && user) {
        return "live";
    }

    // Default to live for safety if signed in, demo if not on an app route
    return user ? "live" : "demo";
}

/**
 * Helper to get the correct display label for the environment
 */
export function getEnvironmentLabel(state: EnvironmentState): string {
    return state === "live" ? "ENVIRONMENT: LIVE" : "ENVIRONMENT: DEMO";
}

/**
 * Deployment environment: where the code is running
 */
export type DeploymentEnvironment = "vercel" | "localhost" | "docker" | "unknown";

/**
 * Detects the deployment environment (Vercel, localhost, Docker, etc.)
 * Helps distinguish between local development and production deployments
 */
export function getDeploymentEnvironment(): DeploymentEnvironment {
    // Check if running on Vercel
    if (typeof window !== "undefined") {
        const hostname = window.location.hostname;
        
        if (hostname === "localhost" || hostname === "127.0.0.1") {
            return "localhost";
        }
        
        if (hostname.includes("vercel.app") || hostname.includes("aegis-foundation-kit")) {
            return "vercel";
        }
        
        // Check for Docker container patterns
        if (hostname.includes(".local") || hostname === "app") {
            return "docker";
        }
    }
    
    return "unknown";
}

/**
 * Helper to get deployment environment label
 */
export function getDeploymentLabel(env: DeploymentEnvironment): string {
    const labels: Record<DeploymentEnvironment, string> = {
        vercel: "Vercel Production",
        localhost: "Local Development",
        docker: "Docker Container",
        unknown: "Unknown Environment"
    };
    return labels[env];
}

/**
 * Checks if this is a production deployment
 */
export function isProductionDeployment(): boolean {
    return getDeploymentEnvironment() === "vercel";
}

/**
 * Checks if this is a local development environment
 */
export function isLocalDevelopment(): boolean {
    return getDeploymentEnvironment() === "localhost";
}
